/**
 * PostHog analytics atoms and helpers
 *
 * Provides PostHog initialization, a reactive atom for the client instance,
 * and a convenience `trackEvent` wrapper. Mirrors the `clerkSessionRef` pattern
 * for synchronous access.
 *
 * @since 1.0.0
 */
import { Atom } from "@effect-atom/atom-react";
import { Effect } from "effect";
// Type-only import — erased at compile time, so it adds nothing to the runtime
// bundle. The `posthog-js` *value* is loaded lazily via `import("posthog-js")`
// inside `initPostHog` (see below) so it never rides the initial `/` graph:
// ~87% of it is unused at paint and analytics never needs to beat the LCP.
import type { CaptureResult, PostHog } from "posthog-js";
// Type-only import — erased at compile time. The `web-vitals` *value* is loaded
// lazily via `import("web-vitals")` alongside `posthog-js` inside `initPostHog`,
// so it never rides the initial `/` graph (see below).
import type { Metric } from "web-vitals";
import { getRoutePrefixForHostname } from "~/config/domains.ts";
import { hasAnalyticsConsent } from "./cookie-consent.ts";

type PostHogClient = PostHog;

/**
 * The subset of the `web-vitals` module surface the reporter needs: the five
 * standard observer-registration functions. Kept as an explicit shape (rather
 * than the whole module) so `reportWebVitals` can be driven by injected fakes in
 * tests — no browser, no real PerformanceObserver.
 *
 * @since 1.0.0
 */
export interface WebVitalsRegistrars {
  onCLS: (report: (metric: Metric) => void) => void;
  onFCP: (report: (metric: Metric) => void) => void;
  onINP: (report: (metric: Metric) => void) => void;
  onLCP: (report: (metric: Metric) => void) => void;
  onTTFB: (report: (metric: Metric) => void) => void;
}

/**
 * Schedule a task for when the main thread is idle.
 *
 * Prefers `requestIdleCallback` so analytics bootstrap yields to paint/LCP;
 * falls back to a short `setTimeout` where it is unavailable (Safari, SSR
 * shims, jsdom).
 *
 * @since 1.0.0
 */
const scheduleIdle = (task: () => void): void => {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(() => task());
  } else {
    setTimeout(task, 1);
  }
};

/**
 * Guards against scheduling the idle import/init more than once across the many
 * `PostHogSync` renders that call `initPostHog()` before the idle work
 * completes. Reset once the client is stored on `posthogRef`, after which the
 * "already initialized" guard takes over.
 */
let idleInitScheduled = false;

/**
 * Brand slug for analytics, derived from the shared domain registry.
 *
 * Resolves a hostname to its route prefix via `getRoutePrefixForHostname` (the
 * web package's host→route-prefix registry, which honors the suffix rules) and
 * strips the leading `/` — e.g. `clawinstall.ai` → `"clawinstall"`,
 * `flirtmachine.app` → `"flirtmachine"`, `orca.test` and
 * `inf-123.orcaloops.ai` → `"orca"`. Unmapped hosts fall back to `"unknown"`.
 *
 * @since 1.0.0
 */
export const brandFromHostname = (hostname: string): string => {
  const prefix = getRoutePrefixForHostname(hostname);
  return prefix ? prefix.slice(1) : "unknown";
};

/**
 * Onboarding/reseller token-URL sanitizer for analytics (CLA-862).
 *
 * The customer magic-link token rides the `/onboarding/start/<token>` landing
 * path (and, pre-migration, `?token=`/`?email=` query params). `history_change`
 * pageview capture would otherwise send that payment-/connection-capable
 * credential to PostHog on every SPA navigation. These pure helpers scrub it
 * from every URL-bearing analytics property before capture, for both the
 * customer (`/clawinstall/onboarding/start/…`) and reseller
 * (`/clawinstall/reseller/onboarding/start/…`) flows.
 *
 * @since 1.0.0
 */

/**
 * The analytics property keys that carry a URL / path / referrer PostHog can
 * capture. Each is sanitized before the event leaves the browser.
 *
 * @since 1.0.0
 */
export const SANITIZED_URL_PROPERTY_KEYS = [
  "$current_url",
  "$pathname",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
] as const;

/**
 * Query params that must never reach analytics: the magic-link token and the
 * intake email forwarded alongside it.
 *
 * @since 1.0.0
 */
const SANITIZED_QUERY_PARAMS: ReadonlyArray<string> = ["token", "email"];

/**
 * Mask the `/onboarding/start/<token>` path segment to `/onboarding/start/:token`
 * (covers the reseller path too — both end in `/onboarding/start/<token>`).
 * Leaves any non-matching value untouched.
 *
 * @since 1.0.0
 */
export const maskOnboardingStartPath = (value: string): string =>
  value.replace(/(\/onboarding\/start\/)[^/?#]+/g, "$1:token");

/**
 * Strip the `token`/`email` params from any query string embedded in `value`,
 * preserving the path, the surviving params, and any `#fragment`. A value with
 * no query string is returned unchanged.
 *
 * @since 1.0.0
 */
export const stripTrackedQueryParams = (value: string): string => {
  const hashIndex = value.indexOf("#");
  const hash = hashIndex === -1 ? "" : value.slice(hashIndex);
  const withoutHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const queryIndex = withoutHash.indexOf("?");
  if (queryIndex === -1) return value;
  const base = withoutHash.slice(0, queryIndex);
  const kept = withoutHash
    .slice(queryIndex + 1)
    .split("&")
    .filter((pair) => !SANITIZED_QUERY_PARAMS.includes(pair.split("=")[0]));
  return kept.length > 0 ? `${base}?${kept.join("&")}${hash}` : `${base}${hash}`;
};

/**
 * Full sanitizer for a single URL/path/referrer string: drop tracked query
 * params, then mask the onboarding token path segment.
 *
 * @since 1.0.0
 */
export const sanitizeAnalyticsUrl = (value: string): string => maskOnboardingStartPath(stripTrackedQueryParams(value));

/**
 * Return a copy of an analytics property bag with every URL-bearing key
 * ({@link SANITIZED_URL_PROPERTY_KEYS}) sanitized. Non-string values are left as
 * they are.
 *
 * @since 1.0.0
 */
export const sanitizeAnalyticsProperties = (
  properties: Record<string, unknown>,
): Record<string, unknown> =>
  SANITIZED_URL_PROPERTY_KEYS.reduce<Record<string, unknown>>(
    (acc, key) => {
      const value = acc[key];
      return typeof value === "string" ? { ...acc, [key]: sanitizeAnalyticsUrl(value) } : acc;
    },
    { ...properties },
  );

/**
 * PostHog `before_send` hook: sanitize the captured event's properties in place
 * (returning a new event) so no token/email ever leaves the browser. Passes a
 * dropped event (`null`) through untouched.
 *
 * @since 1.0.0
 */
export const sanitizeAnalyticsBeforeSend = (result: CaptureResult | null): CaptureResult | null =>
  result === null ? result : { ...result, properties: sanitizeAnalyticsProperties(result.properties) };

/**
 * Mutable ref for synchronous access to the PostHog client.
 *
 * Updated by `initPostHog()`. Components that only need fire-and-forget
 * event capture can use `trackEvent()` which reads from this ref.
 *
 * @since 1.0.0
 */
export const posthogRef: { current: PostHogClient | null } = { current: null };

/**
 * Writable atom holding the PostHog client instance.
 *
 * Updated by `PostHogSync` after initialization so downstream atoms
 * can reactively depend on the analytics client being ready.
 *
 * @since 1.0.0
 */
export const posthogClientAtom = Atom.make<PostHogClient | null>(null);

/**
 * Register the five Core Web Vitals observers, each reporting its finalized
 * metric as a `web_vital` event via the injected `track` function.
 *
 * Pure and injectable: the observer-registration functions and the `track`
 * sink are passed in, so tests can drive it with fakes (capture the callback,
 * invoke it with a synthetic metric, assert the payload) without a browser or a
 * real `PerformanceObserver`. The emitted payload carries
 * `{ metric, value, rating, navigationType, path }`; the `brand` super-property
 * rides automatically because `track` funnels through `posthog.capture`.
 *
 * `path` is read at report time (`window.location.pathname`) so the event
 * records the route the metric finalized on, not the route at registration.
 *
 * Called only from the fresh-init seam in `initPostHog` (after `posthog.init`),
 * which runs once per session — the existing `posthogRef.current` guard makes a
 * consent revoke/re-grant resume without re-registering the observers.
 *
 * @since 1.0.0
 */
export const reportWebVitals = (
  registrars: WebVitalsRegistrars,
  track: (name: string, properties?: Record<string, unknown>) => void,
): void => {
  const report = (metric: Metric): void => {
    track("web_vital", {
      metric: metric.name,
      value: metric.value,
      rating: metric.rating,
      navigationType: metric.navigationType,
      path: window.location.pathname,
    });
  };

  registrars.onLCP(report);
  registrars.onCLS(report);
  registrars.onINP(report);
  registrars.onFCP(report);
  registrars.onTTFB(report);
};

/**
 * Idempotent, SSR-safe, consent-gated PostHog initialization at browser idle.
 *
 * `posthog-js` is loaded via a dynamic `import("posthog-js")` scheduled behind
 * `requestIdleCallback` (with a `setTimeout` fallback), so the library stays
 * off the critical `/` graph and boots only after paint. The dynamic import is
 * run as an Effect fiber (`Effect.runFork`) — the codebase forbids raw Promise
 * chaining. Behavior:
 *
 * - no-op during SSR (`typeof window === "undefined"`)
 * - no-op (no import, no init) until the visitor has explicitly accepted
 *   analytics cookies (GDPR/ePrivacy — see `cookie-consent.ts`)
 * - no-op (after warning) if `VITE_POSTHOG_KEY` is missing
 * - already initialized: opts capture back in if it was revoked earlier in the
 *   session, then returns without re-importing (does NOT invoke `onReady`)
 * - already scheduled: returns without scheduling a second idle import
 * - otherwise: schedules the idle import and, once init completes, stores the
 *   client on `posthogRef` and invokes `onReady(client)`
 *
 * `onReady` fires exactly once, on the fresh idle initialization, so callers
 * (e.g. `PostHogSync`) can update reactive state without a double-set.
 *
 * @since 1.0.0
 */
export const initPostHog = (onReady?: (client: PostHogClient) => void): void => {
  // SSR guard
  if (typeof window === "undefined") {
    return;
  }

  // Consent gate — analytics cookies must be explicitly accepted
  if (!hasAnalyticsConsent()) {
    return;
  }

  // Already initialized — idempotent; resume capture after an in-session revoke
  if (posthogRef.current) {
    if (posthogRef.current.has_opted_out_capturing()) {
      posthogRef.current.opt_in_capturing();
    }
    return;
  }

  // An idle init is already scheduled from an earlier render — don't import or
  // initialize posthog-js twice.
  if (idleInitScheduled) {
    return;
  }

  const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const apiHost = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://us.i.posthog.com";

  if (!apiKey) {
    console.warn("[PostHog] VITE_POSTHOG_KEY is not set — analytics disabled");
    return;
  }

  idleInitScheduled = true;
  scheduleIdle(() => {
    Effect.runFork(
      Effect.all(
        [
          Effect.promise(() => import("posthog-js")),
          // Loaded lazily alongside posthog-js so the observers stay off the
          // critical `/` graph and boot only after paint, same as analytics.
          Effect.promise(() => import("web-vitals")),
        ] as const,
      ).pipe(
        Effect.map(([{ default: posthog }, webVitals]) => {
          posthog.init(apiKey, {
            api_host: apiHost,
            // "history_change" captures SPA (history-API) navigations, not just
            // the initial load — TanStack Router navigations are history pushes,
            // so `true` dropped every post-load pageview.
            capture_pageview: "history_change",
            capture_pageleave: "if_capture_pageview",
            autocapture: true,
            // Scrub the onboarding/reseller magic-link token (and intake email)
            // out of every captured URL/path/referrer before the event leaves the
            // browser (CLA-862). `history_change` would otherwise send the raw
            // `/onboarding/start/<token>` navigation to PostHog.
            before_send: sanitizeAnalyticsBeforeSend,
          });

          // Super property — rides on every event ($pageview, autocapture,
          // trackEvent), so all traffic can be segmented by brand in one project.
          posthog.register({ brand: brandFromHostname(window.location.hostname) });

          posthogRef.current = posthog;
          idleInitScheduled = false;

          // Real-user Core Web Vitals → `web_vital` events. Registered on the
          // fresh-init path only (this block runs once per session), so a
          // consent revoke/re-grant — which returns early above at the
          // `posthogRef.current` guard — never double-registers the observers.
          reportWebVitals(webVitals, trackEvent);

          onReady?.(posthog);
        }),
      ),
    );
  });
};

/**
 * Stop all PostHog capture after a consent revoke.
 *
 * Opts out of capturing (autocapture, pageviews, manual events) and resets
 * the identified user so no further data is associated with them. No-op when
 * PostHog was never initialized (e.g. visitor declined from the start).
 *
 * @since 1.0.0
 */
export const disablePostHog = (): void => {
  const client = posthogRef.current;
  if (!client) {
    return;
  }
  client.opt_out_capturing();
  client.reset();
};

/**
 * Fire-and-forget event capture.
 *
 * No-op when PostHog is not initialized (SSR, missing key, etc.) or when the
 * visitor has not accepted analytics cookies.
 *
 * @since 1.0.0
 */
export const trackEvent = (name: string, properties?: Record<string, unknown>): void => {
  if (!hasAnalyticsConsent()) {
    return;
  }
  posthogRef.current?.capture(name, properties);
};
